import { useCallback, useId } from 'react'
import type { PluginSettings } from '@/data/dropPlugins'

const FORMAT_OPTIONS = [
  { value: 'original', label: 'Original (keep source format)' },
  { value: 'webp', label: 'WebP' },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'png', label: 'PNG' },
] as const

const RESOLUTION_PRESETS = [
  { value: '', label: 'Original (no resize)' },
  { value: '4000', label: '4K (4000px)' },
  { value: '2000', label: '2K (2000px)' },
  { value: '1920', label: 'Full HD (1920px)' },
] as const

interface ProcessingSettingsProps {
  settings: PluginSettings
  onChange: (settings: PluginSettings) => void
  disabled?: boolean
  bare?: boolean
}

export function ProcessingSettings({ settings, onChange, disabled, bare }: ProcessingSettingsProps) {
  const id = useId()
  const formatId = `${id}-format`
  const resolutionId = `${id}-resolution`
  const qualityId = `${id}-quality`

  const resolution = settings.maxWidth ? String(settings.maxWidth) : ''
  const format = settings.format || 'original'
  // Default quality is 90 — omitting the key from settings means "use default"
  const quality = settings.quality || 90

  const update = useCallback(
    (partial: Partial<PluginSettings>) => {
      onChange({ ...settings, ...partial })
    },
    [settings, onChange]
  )

  const onResolutionChange = (value: string) => {
    if (value === '') {
      const { maxWidth: _maxWidth, maxHeight: _maxHeight, ...rest } = settings
      onChange(rest)
    } else {
      const px = Number(value)
      update({ maxWidth: px, maxHeight: px })
    }
  }

  const onFormatChange = (value: string) => {
    if (value === 'original') {
      const { format: _format, ...rest } = settings
      onChange(rest)
    } else {
      update({ format: value as PluginSettings['format'] })
    }
  }

  const onQualityChange = (value: number) => {
    if (value === 90) {
      const { quality: _quality, ...rest } = settings
      onChange(rest)
    } else {
      update({ quality: value })
    }
  }

  const content = (
    <>
      <div>
        <label htmlFor={formatId} className="block text-sm font-medium mb-2">
          Output Format
        </label>
        <select
          id={formatId}
          value={format}
          onChange={(e) => onFormatChange(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        >
          {FORMAT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={resolutionId} className="block text-sm font-medium mb-2">
          Max Resolution
        </label>
        <select
          id={resolutionId}
          value={resolution}
          onChange={(e) => onResolutionChange(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        >
          {RESOLUTION_PRESETS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={qualityId} className="block text-sm font-medium mb-2">
          Quality: {quality}
        </label>
        <input
          id={qualityId}
          type="range"
          min={1}
          max={100}
          value={quality}
          onChange={(e) => onQualityChange(Number(e.target.value))}
          disabled={disabled}
          className="w-full disabled:opacity-50"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>Smaller file</span>
          <span>Higher quality</span>
        </div>
      </div>
    </>
  )

  if (bare) return <div className="space-y-6">{content}</div>

  return (
    <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-1">Processing Settings</h3>
        <p className="text-xs text-muted-foreground">Configure how uploaded photos are processed</p>
      </div>
      {content}
    </div>
  )
}
